/**
 * GlUnmatched — écritures du compte sans ligne de relevé (« en circulation ») : date de début de conciliation, marquage manuel
 * « passée en banque » (une ou plusieurs), liste des écritures marquées (réversible).
 */
import { useCallback, useEffect, useState } from "react";
import { ArrowRight, Check, CheckCheck, Loader2 } from "lucide-react";
import { api } from "@/api";
import { toast } from "@/lib/toast";
import { Btn, errMsg, money } from "../../comptabilite/_shared";
import BankStartDate from "./BankStartDate";
import BankCleared from "./BankCleared";

const EMPTY = { items: [], total: 0, count: 0, cleared: [], before_start: { count: 0, total: 0 }, start_date: null, from_date: null };

export default function GlUnmatched({ base, accountId, reloadKey, goTab, changed }) {
  const [data, setData] = useState(null);
  const [sel, setSel] = useState(new Set());
  const [busy, setBusy] = useState(false);
  const [showCleared, setShowCleared] = useState(false);

  const load = useCallback(async () => {
    try { const r = await api.get(`${base}/bank/gl-unmatched`, { params: { account_id: accountId, with_cleared: true } }); setData(r.data); setSel(new Set()); }
    catch { setData(EMPTY); }
  }, [base, accountId]);
  useEffect(() => { load(); }, [load, reloadKey]);

  const refresh = () => { load(); changed?.(); };
  const toggle = (id) => setSel((p) => { const n = new Set(p); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  const clear = async (ids) => {
    if (!ids.length) return;
    if (!window.confirm(`Marquer ${ids.length} écriture(s) comme passée(s) en banque sans ligne de relevé ?\nElles ne seront plus « en circulation » (réversible).`)) return;
    setBusy(true);
    try { const r = await api.post(`${base}/bank/gl/clear`, { account_id: accountId, entry_ids: ids }); toast.success(`${r.data.cleared} écriture(s) marquée(s) passée(s) en banque`); refresh(); }
    catch (e) { toast.error(errMsg(e)); }
    finally { setBusy(false); }
  };

  const items = data?.items || [];
  return (
    <div className="bg-amber-50/50 border border-amber-200 rounded-xl overflow-hidden" data-testid="bank-gl-unmatched">
      <div className="px-3 py-2 flex items-center gap-2 flex-wrap text-xs text-amber-900">
        <strong>Écritures du compte sans ligne de relevé</strong> — chèques en circulation, dépôts en transit ou pièces à vérifier
        {data && <span className="ml-auto" data-testid="bank-gl-unmatched-count">{data.count} écriture(s) · <strong className="font-mono">{money(data.total)}</strong></span>}
        <button onClick={() => goTab("grand-livre", "journal")} className="text-teal-700 hover:underline inline-flex items-center gap-1" data-testid="bank-gl-unmatched-link">Journal général <ArrowRight className="w-3 h-3" /></button>
      </div>
      {data && <BankStartDate base={base} accountId={accountId} startDate={data.start_date} before={data.before_start} onSaved={refresh} />}
      {data === null ? <div className="p-4 text-center text-slate-400"><Loader2 className="w-4 h-4 animate-spin mx-auto" /></div>
        : items.length === 0 ? <p className="px-3 py-2 text-xs text-amber-800/70 border-t bg-white" data-testid="bank-gl-unmatched-empty">Toutes les écritures du compte{data.from_date ? ` depuis le ${data.from_date}` : ""} ont une ligne de relevé correspondante.</p>
        : (
          <>
            <div className="px-3 py-1.5 bg-white border-t flex items-center gap-2 flex-wrap text-xs">
              <label className="inline-flex items-center gap-1.5 cursor-pointer text-slate-700">
                <input type="checkbox" checked={sel.size > 0 && sel.size === items.length} onChange={(e) => setSel(e.target.checked ? new Set(items.map((i) => i.entry_id)) : new Set())} className="accent-teal-600" data-testid="bank-gl-select-all" /> Tout
              </label>
              <Btn tone="sky" icon={CheckCheck} onClick={() => clear([...sel])} busy={busy} disabled={!sel.size} testId="bank-gl-clear-selected">Marquer passée(s) en banque ({sel.size})</Btn>
              <span className="text-slate-500">pour les écritures déjà passées en banque avant le premier relevé, ou sans ligne (frais, soldes de départ).</span>
            </div>
            <div className="max-h-64 overflow-y-auto bg-white border-t">
              <table className="w-full text-xs" data-testid="bank-gl-unmatched-table">
                <tbody className="divide-y">
                  {items.map((e) => (
                    <tr key={e.entry_id} className={sel.has(e.entry_id) ? "bg-sky-50" : ""} data-testid={`bank-gl-entry-${e.entry_id}`}>
                      <td className="px-3 py-1.5 w-8"><input type="checkbox" checked={sel.has(e.entry_id)} onChange={() => toggle(e.entry_id)} className="accent-teal-600" data-testid={`bank-gl-check-${e.entry_id}`} /></td>
                      <td className="px-2 py-1.5 whitespace-nowrap text-slate-600 w-24">{e.date}</td>
                      <td className="px-2 py-1.5 w-16 text-slate-500">{e.no ? `N° ${e.no}` : <span className="text-orange-700">GL att.</span>}</td>
                      <td className="px-2 py-1.5 w-12 text-slate-500">{e.journal}</td>
                      <td className="px-2 py-1.5 text-slate-800 truncate max-w-[400px]" title={e.memo}>{e.memo}{e.reference ? <span className="text-slate-400"> · {e.reference}</span> : ""}</td>
                      <td className={`px-3 py-1.5 text-right font-mono font-semibold w-28 ${e.amount < 0 ? "text-rose-700" : "text-emerald-700"}`}>{money(e.amount)}</td>
                      <td className="px-2 py-1.5 w-10 text-right"><button onClick={() => clear([e.entry_id])} disabled={busy} className="text-teal-700 hover:text-teal-900 p-1 disabled:opacity-50" title="Marquer passée en banque" data-testid={`bank-gl-clear-${e.entry_id}`}><Check className="w-3.5 h-3.5" /></button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      {data && (
        <div className="px-3 py-1.5 border-t bg-white text-xs">
          <button onClick={() => setShowCleared((v) => !v)} className="text-slate-600 hover:underline" data-testid="bank-toggle-cleared">{showCleared ? "Masquer" : "Voir"} les écritures marquées passées en banque ({data.cleared?.length || 0})</button>
        </div>
      )}
      {data && showCleared && <BankCleared base={base} accountId={accountId} items={data.cleared} onChanged={refresh} />}
    </div>
  );
}
